import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { InjectedFormProps, reduxForm } from 'redux-form';

import VersionsSection from 'dashboard/components-lib/VersionsSection';
import { workflowVersionMetadata } from 'dashboard/components-lib/VersionsSection/utils';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';

const WORKFLOW_VERSION_DESCRIPTION_FORM_NAME = 'updateWorkflowVersionDescription';
const DESCRIPTION_CHARACTER_LIMIT = 50000;

interface FormUpdateWorkflowVersionDescriptionData {
  description: string;
}

interface WorkflowVersionsSectionProps {
  currentVersionId: string;
  isDirty?: boolean;
  isLoadingVersionList: boolean;
  latestDraftVersionId: string;
  versionsList: (WorkflowVersion | WorkflowBlock)[];
  workflowDeployment?: WorkflowDeployment;
  closeWorkflowVersionView: () => void;
  loadWorkflowVersion: (workflowVersionId: string) => void;
  loadVersionList: () => Promise<void>;
  restoreDraftVersion: () => Promise<void>;
  handleDiscardDraft: () => Promise<void>;
  updateVersionDescription: (description: string) => void;
}

const WorkflowVersionsSection = ({
  currentVersionId,
  isDirty,
  isLoadingVersionList,
  latestDraftVersionId,
  versionsList,
  workflowDeployment,
  closeWorkflowVersionView,
  loadWorkflowVersion,
  loadVersionList,
  restoreDraftVersion,
  handleDiscardDraft,
  updateVersionDescription,
}: WorkflowVersionsSectionProps &
  InjectedFormProps<FormUpdateWorkflowVersionDescriptionData, WorkflowVersionsSectionProps>) => {
  useOnMountAndUnmount(loadVersionList);

  const versionMetadata = useMemo(() => {
    return workflowVersionMetadata(latestDraftVersionId, versionsList, workflowDeployment);
  }, [versionsList, workflowDeployment, latestDraftVersionId]);

  return (
    <VersionsSection
      versions={versionMetadata}
      hide={closeWorkflowVersionView}
      currentVersionId={currentVersionId}
      isFormPristine={!isDirty}
      formName={WORKFLOW_VERSION_DESCRIPTION_FORM_NAME}
      isLoading={isLoadingVersionList}
      handleAddLocalDraftVersion={restoreDraftVersion}
      handleDiscardDraft={handleDiscardDraft}
      handleSelectVersion={loadWorkflowVersion}
      setDescription={updateVersionDescription}
    />
  );
};

export default reduxForm<FormUpdateWorkflowVersionDescriptionData, WorkflowVersionsSectionProps>({
  form: WORKFLOW_VERSION_DESCRIPTION_FORM_NAME,
  enableReinitialize: true,
  validate: ({ description }) =>
    !isEmpty(description) && description.length > DESCRIPTION_CHARACTER_LIMIT
      ? { description: `Must be ${DESCRIPTION_CHARACTER_LIMIT} characters or less` }
      : {},
})(WorkflowVersionsSection);
